import { useLayoutEffect, useRef } from "react";

// Round 10 #1: the phone's Back button must close the top-most modal/sheet,
// not navigate away underneath it. Each open modal pushes a history entry;
// popstate closes only the top of the stack. Closing via X/backdrop pops our
// entry so history stays clean.
// Round 12 #1: forms with typed input ask "Discard changes?" before Back/Escape
// closes them. Round 12 #2: subscribers (AppShell) hide the bottom nav whenever
// any modal is open.

const stack = [];
let listening = false;
let skipNextPop = false;
let formDirty = false;
const subs = new Set();

const discardMsg = () =>
  (navigator.language || "").startsWith("es") ? "¿Descartar los cambios?" : "Discard changes?";

export function setFormDirty(v) { formDirty = !!v; }

export function isFormDirty() { return formDirty; }

// Round 13 #1: app-styled discard dialog replaces the native confirm when the
// host (AppShell) registers a handler; falls back to window.confirm otherwise.
let discardHandler = null;
export function setDiscardHandler(fn) { discardHandler = fn; }

export function onModalStackChange(fn) {
  subs.add(fn);
  fn(stack.length > 0);
  return () => subs.delete(fn);
}

function notify() { subs.forEach((f) => f(stack.length > 0)); }

export function _debugStack() { return { size: stack.length, histLen: window.history.length, state: window.history.state }; }

function confirmDiscard() {
  if (!formDirty) return true;
  if (discardHandler) return discardHandler(); // Promise<boolean> — styled in-app dialog
  if (window.confirm(discardMsg())) { formDirty = false; return true; }
  return false;
}

// DialogContent calls this on Escape when dirty (Radix close is prevented first).
export function requestTopClose() {
  const top = stack[stack.length - 1];
  if (!top) return;
  const res = confirmDiscard();
  if (res === true) { formDirty = false; top.onClose(); }
  else if (res !== false) res.then((ok) => { if (ok) { formDirty = false; top.onClose(); } });
}

function ensureListener() {
  if (listening) return;
  listening = true;
  window.addEventListener("popstate", () => {
    if (skipNextPop) { skipNextPop = false; return; } // our own programmatic back()
    const top = stack[stack.length - 1];
    if (!top) return;
    const finish = () => { formDirty = false; stack.pop(); notify(); top.onClose(); };
    const cancel = () => window.history.pushState({ ...(window.history.state || {}), bfModal: true }, ""); // cancel the navigation, keep the form
    const res = confirmDiscard();
    if (res === true) finish();
    else if (res === false) cancel();
    else res.then((ok) => (ok ? finish() : cancel())); // styled async dialog
  });
}

export function useModalBack(open, onClose) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const pendingBackRef = useRef(null);
  // useLayoutEffect: the entry must exist before paint so a fast Back press can't
  // beat the push (Round 12 #3). Preserve router state (idx) so React Router's
  // pop handling stays consistent across our entries.
  useLayoutEffect(() => {
    if (!open) return;
    if (pendingBackRef.current) { clearTimeout(pendingBackRef.current); pendingBackRef.current = null; } // StrictMode remount / rapid reopen
    ensureListener();
    const entry = { onClose: () => onCloseRef.current() };
    stack.push(entry);
    window.history.pushState({ ...(window.history.state || {}), bfModal: true }, "");
    notify();
    const onKey = (e) => {
      if (e.key !== "Escape" || stack[stack.length - 1] !== entry) return;
      const res = confirmDiscard();
      if (res === true) { formDirty = false; onCloseRef.current(); }
      else if (res !== false) res.then((ok) => { if (ok) { formDirty = false; onCloseRef.current(); } });
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      const wasTop = stack[stack.length - 1] === entry;
      const i = stack.indexOf(entry);
      if (i !== -1) stack.splice(i, 1);
      notify();
      // Closed via X/backdrop while our entry is still current → consume it.
      // Deferred + cancellable: a StrictMode remount re-runs the effect in the
      // same commit and cancels this, so dev mode never fires a phantom back().
      if (wasTop && window.history.state?.bfModal) {
        pendingBackRef.current = setTimeout(() => {
          pendingBackRef.current = null;
          if (window.history.state?.bfModal) {
            skipNextPop = true;
            window.history.back();
          }
        }, 0);
      }
    };
  }, [open]);
}
